import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Copy, ExternalLink, MailPlus } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import {
  SendInviteDialog,
  SendResetButton,
} from "@/components/admin/UserAccessActions";
import { hasAuthAccount } from "@/lib/adminUserAccess";


interface Payload {
  profile: any;
  roles: string[];
  una: any;
  una_metadata: any;
  journey: {
    state: any;
    total_steps: number;
    done_steps: number;
    percent: number;
    recent_completions: any[];
    last_progress_at: string | null;
  };
  formation: {
    una_confirmed_at: string | null;
    ein: string | null;
    first_meeting_at: string | null;
    stages: any[];
  };
  documents: any[];
  governance: { score: number | null; open_items: any[] };
  tasks: { open_count: number; overdue_count: number; recent: any[] };
  questions: any[];
  tickets: any[];
  stew_sessions_recent: number;
  recent_activity: any[];
  notes: any[];
}

function SectionCard({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="space-y-2">{children}</CardContent>
    </Card>
  );
}

function ErrorBox({ label }: { label: string }) {
  return (
    <p className="text-sm text-destructive">
      Could not load {label}. Try refreshing.
    </p>
  );
}

export default function MemberDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { toast } = useToast();
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [recordId, setRecordId] = useState<string | null | undefined>(undefined);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (supabase as any)
      .from("member_records")
      .select("id")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }: any) => {
        if (!cancelled) setRecordId(data?.id ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const memberEmail: string = (data?.profile?.email as string) || "";

  useEffect(() => {
    if (!memberEmail) return;
    let cancelled = false;
    hasAuthAccount(memberEmail).then((res) => {
      if (!cancelled) setHasAccount(res);
    });
    return () => {
      cancelled = true;
    };
  }, [memberEmail]);


  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const { data: payload, error } = await supabase.rpc("admin_member_360", {
        _member_id: userId,
      });
      if (error) throw error;
      setData(payload as any);
    } catch (err: any) {
      console.error("Member 360 error:", err);
      setLoadError(err?.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  async function saveNote() {
    if (!note.trim() || !userId) return;
    setSavingNote(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const author = userRes?.user?.id;
      if (!author) throw new Error("Not signed in");
      const { error } = await supabase.from("admin_member_notes").insert({
        member_user_id: userId,
        author_user_id: author,
        body: note.trim(),
      });
      if (error) throw error;
      setNote("");
      await load();
      toast({ title: "Note saved" });
    } catch (err: any) {
      toast({
        title: "Could not save note",
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSavingNote(false);
    }
  }

  function copyEmail(email?: string) {
    if (!email) return;
    navigator.clipboard.writeText(email);
    toast({ title: "Email copied" });
  }

  if (recordId) {
    return <Navigate to={`/admin/members/${recordId}`} replace />;
  }

  if (loading || recordId === undefined) {
    return (
      <div className="container mx-auto p-4 md:p-6 space-y-4">
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-destructive">
              Could not load this member. {loadError}
            </p>
            <Button className="mt-3" variant="outline" onClick={load}>
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const p = data.profile || {};
  const una = data.una || {};

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-4 md:p-6 space-y-3">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="space-y-1">
              <h1 className="text-2xl md:text-3xl font-bold">
                {p.full_name || "No name"}
              </h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{p.email}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => copyEmail(p.email)}
                  title="Copy email"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              {una?.name && (
                <p className="text-sm">
                  Association:{" "}
                  <span className="font-medium">{una.name}</span>
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                This account has no member record.
              </p>
              <div className="flex flex-wrap gap-1 pt-1">
                {(data.roles || []).map((r) => (
                  <Badge key={r} variant="outline">
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2 md:text-right">
              <div className="flex flex-wrap gap-2 md:justify-end">
                {hasAccount === false && (
                  <Button size="sm" onClick={() => setInviteOpen(true)}>
                    <MailPlus className="h-4 w-4 mr-2" />
                    Send invite
                  </Button>
                )}
                {hasAccount === true && <SendResetButton email={p.email} />}
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>
                  Joined:{" "}
                  {p.created_at ? format(new Date(p.created_at), "PP") : "n/a"}
                </p>
                <p>
                  Last sign in:{" "}
                  {p.last_login
                    ? formatDistanceToNow(new Date(p.last_login), {
                        addSuffix: true,
                      })
                    : "never"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <SendInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        defaultEmail={p.email || ""}
        defaultName={p.full_name || ""}
      />


      <div className="grid gap-4 md:grid-cols-2">
        {/* Journey */}
        <SectionCard title="Journey">
          {(() => {
            try {
              const j = data.journey;
              const stalled =
                !j.state?.journey_completed &&
                j.last_progress_at &&
                Date.now() - new Date(j.last_progress_at).getTime() >
                  14 * 24 * 60 * 60 * 1000;
              return (
                <>
                  <div className="flex items-center gap-2">
                    <p className="text-sm">
                      Phase {j.state?.current_phase ?? "n/a"} , {j.percent}%
                      complete ({j.done_steps}/{j.total_steps} steps)
                    </p>
                    {stalled && (
                      <Badge variant="destructive">Stalled 14+ days</Badge>
                    )}
                    {j.state?.journey_completed && (
                      <Badge variant="secondary">Journey complete</Badge>
                    )}
                  </div>
                  <Separator className="my-2" />
                  <p className="text-xs font-medium text-muted-foreground">
                    Recent step completions
                  </p>
                  {j.recent_completions?.length ? (
                    <ul className="text-sm space-y-1">
                      {j.recent_completions.map((s: any) => (
                        <li key={s.step_id} className="flex justify-between">
                          <span className="truncate mr-2">
                            {s.step_title || "Step"}
                          </span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {s.completed_at
                              ? formatDistanceToNow(new Date(s.completed_at), {
                                  addSuffix: true,
                                })
                              : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No steps completed yet.
                    </p>
                  )}
                </>
              );
            } catch {
              return <ErrorBox label="journey" />;
            }
          })()}
        </SectionCard>

        {/* Formation & documents */}
        <SectionCard title="Formation and documents">
          {(() => {
            try {
              const f = data.formation;
              return (
                <>
                  <ul className="text-sm space-y-1">
                    <li>
                      Association confirmed:{" "}
                      {f.una_confirmed_at
                        ? format(new Date(f.una_confirmed_at), "PP")
                        : "not yet"}
                    </li>
                    <li>EIN: {f.ein || "not recorded"}</li>
                    <li>
                      First meeting:{" "}
                      {f.first_meeting_at
                        ? format(new Date(f.first_meeting_at), "PP")
                        : "not yet"}
                    </li>
                  </ul>
                  <Separator className="my-2" />
                  <p className="text-xs font-medium text-muted-foreground">
                    Recent documents
                  </p>
                  {data.documents?.length ? (
                    <ul className="text-sm space-y-1 max-h-56 overflow-auto">
                      {data.documents.slice(0, 10).map((d: any) => (
                        <li key={d.id} className="flex justify-between gap-2">
                          <span className="truncate">{d.document_name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {d.requires_signatures
                              ? `${d.signed_count || 0}/${d.total_signers || 0} signed`
                              : d.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No documents yet.
                    </p>
                  )}
                </>
              );
            } catch {
              return <ErrorBox label="formation" />;
            }
          })()}
        </SectionCard>

        {/* Governance */}
        <SectionCard title="Governance health">
          {(() => {
            try {
              const g = data.governance;
              return (
                <>
                  <p className="text-2xl font-semibold">
                    {g.score ?? "n/a"}
                    <span className="text-sm text-muted-foreground ml-1">
                      /100
                    </span>
                  </p>
                  <p className="text-xs font-medium text-muted-foreground">
                    Open compliance items
                  </p>
                  {g.open_items?.length ? (
                    <ul className="text-sm space-y-1 max-h-48 overflow-auto">
                      {g.open_items.map((i: any) => (
                        <li key={i.id} className="flex justify-between gap-2">
                          <span className="truncate">
                            {i.title || i.rule_key}
                          </span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {i.due_date
                              ? format(new Date(i.due_date), "MMM d")
                              : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No open compliance items.
                    </p>
                  )}
                </>
              );
            } catch {
              return <ErrorBox label="governance" />;
            }
          })()}
        </SectionCard>

        {/* Tasks */}
        <SectionCard
          title="Tasks"
          action={
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/users/tasks?userId=${userId}`}>
                Open tasks <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          }
        >
          {(() => {
            try {
              const t = data.tasks;
              return (
                <>
                  <p className="text-sm">
                    {t.open_count} open , {t.overdue_count} overdue
                  </p>
                  {t.recent?.length ? (
                    <ul className="text-sm space-y-1 max-h-56 overflow-auto">
                      {t.recent.map((tk: any) => (
                        <li key={tk.id} className="flex justify-between gap-2">
                          <span className="truncate">{tk.task_title}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {tk.due_date
                              ? format(new Date(tk.due_date), "MMM d")
                              : tk.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No open tasks.
                    </p>
                  )}
                </>
              );
            } catch {
              return <ErrorBox label="tasks" />;
            }
          })()}
        </SectionCard>

        {/* Questions and support */}
        <SectionCard title="Questions and support">
          {(() => {
            try {
              return (
                <>
                  <p className="text-xs font-medium text-muted-foreground">
                    Congregation questions
                  </p>
                  {data.questions?.length ? (
                    <ul className="text-sm space-y-1 max-h-40 overflow-auto">
                      {data.questions.map((q: any) => (
                        <li key={q.id} className="flex justify-between gap-2">
                          <Link
                            to={
                              q.ticket_id
                                ? `/admin/support/tickets/${q.ticket_id}`
                                : `/admin/support/questions`
                            }
                            className="truncate hover:underline"
                          >
                            {q.question_text?.slice(0, 60) || "Question"}
                          </Link>
                          <Badge variant="outline" className="shrink-0">
                            {q.status}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No questions yet.
                    </p>
                  )}
                  <Separator className="my-2" />
                  <p className="text-xs font-medium text-muted-foreground">
                    Support tickets
                  </p>
                  {data.tickets?.length ? (
                    <ul className="text-sm space-y-1 max-h-40 overflow-auto">
                      {data.tickets.map((t: any) => (
                        <li key={t.id} className="flex justify-between gap-2">
                          <Link
                            to={`/admin/support/tickets/${t.id}`}
                            className="truncate hover:underline"
                          >
                            {t.subject || "Ticket"}
                          </Link>
                          <Badge variant="outline" className="shrink-0">
                            {t.status}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No tickets.
                    </p>
                  )}
                  <Separator className="my-2" />
                  <p className="text-sm">
                    Recent Ask Stew sessions (30 days):{" "}
                    <span className="font-medium">
                      {data.stew_sessions_recent}
                    </span>{" "}
                    <Link
                      to={`/admin/prospect-conversations?userId=${userId}`}
                      className="text-xs underline text-muted-foreground ml-1"
                    >
                      view
                    </Link>
                  </p>
                </>
              );
            } catch {
              return <ErrorBox label="support" />;
            }
          })()}
        </SectionCard>

        {/* Recent activity */}
        <SectionCard title="Recent activity">
          {(() => {
            try {
              return data.recent_activity?.length ? (
                <ul className="text-sm space-y-1 max-h-56 overflow-auto">
                  {data.recent_activity.map((a: any, i: number) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="truncate">
                        {a.label}
                        {a.resource_type ? ` , ${a.resource_type}` : ""}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {a.created_at
                          ? formatDistanceToNow(new Date(a.created_at), {
                              addSuffix: true,
                            })
                          : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No recent activity.
                </p>
              );
            } catch {
              return <ErrorBox label="activity" />;
            }
          })()}
        </SectionCard>
      </div>

      {/* Admin notes */}
      <SectionCard title="Admin notes (internal)">
        <p className="text-xs text-muted-foreground">
          Only admins can see these notes. Members never see them.
        </p>
        <div className="space-y-2">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about this member"
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={saveNote}
              disabled={!note.trim() || savingNote}
            >
              {savingNote ? "Saving..." : "Save note"}
            </Button>
          </div>
        </div>
        <Separator className="my-2" />
        {data.notes?.length ? (
          <ul className="space-y-3 max-h-72 overflow-auto">
            {data.notes.map((n: any) => (
              <li key={n.id} className="text-sm">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{n.author_name || "Admin"}</span>
                  <span>
                    {n.created_at
                      ? formatDistanceToNow(new Date(n.created_at), {
                          addSuffix: true,
                        })
                      : ""}
                  </span>
                </div>
                <p className="whitespace-pre-wrap">{n.body}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No notes yet.</p>
        )}
      </SectionCard>
    </div>
  );
}
